import posthog from '../config/posthog';

// Event categories
export enum EventCategory {
  NAVIGATION = 'navigation',
  USER = 'user',
  CONTENT = 'content',
  POST = 'post',
  BRAND = 'brand',
  PERSONA = 'persona',
  ACCOUNT = 'account',
  SETTINGS = 'settings',
  BILLING = 'billing',
}

interface UserTraits {
  email?: string | null;
  name?: string | null;
  createdAt?: string;
  lastLoginAt?: string;
  role?: string;
  isNewUser?: boolean;
}

interface UserProperties extends Record<string, any> {
  $email?: string | null;
  $name?: string | null;
  role: string;
  last_login: string;
  is_new_user?: boolean;
}

// Check if PostHog is properly initialized
const isPostHogInitialized = () => {
  return typeof posthog !== 'undefined' && 
         posthog.init && // Ensure init has been called/exists
         typeof posthog.capture === 'function'; // Check for a core function
};

// Safely call PostHog methods with error handling
const safePostHogCall = (fnName: string, ...args: any[]) => {
  try {
    if (typeof posthog === 'undefined' || typeof (posthog as any)[fnName] !== 'function') {
      console.warn(`PostHog or method ${fnName} not initialized yet. Operation queued.`);
      // Queue the operation to be executed after initialization
      setTimeout(() => {
        if (typeof posthog !== 'undefined' && typeof (posthog as any)[fnName] === 'function') {
          (posthog as any)[fnName](...args);
        } else {
          console.error(`PostHog or method ${fnName} still not initialized after delay`);
        }
      }, 1000);
      return;
    }
    return (posthog as any)[fnName](...args);
  } catch (error) {
    console.error(`PostHog error calling ${fnName}:`, error);
    return null;
  }
};

// Track a custom event
export const trackEvent = (
  category: EventCategory,
  action: string,
  properties?: Record<string, any>
) => {
  const eventName = `${category}_${action}`;
  // Directly call posthog.capture via safePostHogCall
  safePostHogCall('capture', eventName, {
    category,
    action,
    ...properties,
  });
};

// Identify a user and set their properties
export const identifyUser = (
  userId: string,
  traits: UserTraits = {}
) => {
  // Directly call posthog.identify via safePostHogCall
  safePostHogCall('identify', userId, {
    distinct_id: userId,
    $set: {
      email: traits.email,
      name: traits.name,
      role: traits.role || 'user',
      last_login: traits.lastLoginAt || new Date().toISOString(),
      is_new_user: traits.isNewUser,
      $email: traits.email,
      $name: traits.name,
      $initial_os: 'web',
      $initial_browser: navigator.userAgent,
      $initial_device_type: 'web',
      $initial_referrer: document.referrer,
      $initial_referring_domain: document.referrer ? new URL(document.referrer).hostname : '',
      $initial_current_url: window.location.href,
    },
    $set_once: {
      first_login: new Date().toISOString(),
      created_at: traits.createdAt || new Date().toISOString(),
    }
  });

  // Track login event with all relevant properties
  // This call to trackEvent will internally use the updated safePostHogCall
  trackEvent(EventCategory.USER, 'login', {
    distinct_id: userId,
    user_id: userId,
    email: traits.email,
    name: traits.name,
    is_new_user: traits.isNewUser,
    role: traits.role || 'user',
  });
};

// Reset user identity (for logout)
export const resetIdentity = () => {
  let currentUserId: string | null = null;
  if (typeof posthog !== 'undefined' && typeof posthog.get_distinct_id === 'function') {
    try {
      currentUserId = posthog.get_distinct_id();
    } catch (e) {
      console.warn('PostHog: Could not get distinct_id before reset.', e);
    }
  } else {
    console.warn('PostHog: get_distinct_id function not available when attempting reset.');
  }

  if (currentUserId) {
    trackEvent(EventCategory.USER, 'logout', {
      distinct_id: currentUserId
    });
  } else {
    // If we couldn't get the ID, track logout without it.
    trackEvent(EventCategory.USER, 'logout', {});
  }
  
  // Reset user identification
  safePostHogCall('reset', true); // true parameter ensures we reset all data
};

// Track page view
export const trackPageView = (
  pageName: string,
  properties?: Record<string, any>
) => {
  // Directly call posthog.capture via safePostHogCall
  safePostHogCall('capture', '$pageview', {
    page: pageName,
    ...properties,
  });
};

// Set user properties
export const setUserProperties = (properties: Record<string, any>) => {
  // Check for people.set within safePostHogCall or handle differently
  if (typeof posthog !== 'undefined' && posthog.people && typeof posthog.people.set === 'function') {
    safePostHogCall('people.set', properties); // This won't work as people.set is nested.
                                             // We need to adjust safePostHogCall or handle this case.
                                             // For now, let's assume a direct call after check
    try {
      posthog.people.set(properties);
    } catch (e) {
       console.error('PostHog error calling people.set:', e);
    }

  } else {
    // Fallback to capture event with properties
    safePostHogCall('capture', '$set', properties);
  }
};

// Set company/team properties
export const setCompanyProperties = (
  companyId: string,
  properties: Record<string, any>
) => {
  if (typeof posthog !== 'undefined' && typeof posthog.group === 'function') {
    safePostHogCall('group', 'company', companyId, properties);
  } else {
    console.warn('PostHog group feature not available');
  }
};

// Opt out of tracking (for GDPR compliance)
export const optOut = () => {
  safePostHogCall('opt_out_capturing');
};

// Opt back into tracking
export const optIn = () => {
  safePostHogCall('opt_in_capturing');
};

// Check if tracking is enabled
export const isTrackingEnabled = (): boolean => {
  try {
    // Add check for has_opted_out_capturing existence
    return typeof posthog !== 'undefined' &&
           typeof posthog.has_opted_out_capturing === 'function' &&
           !posthog.has_opted_out_capturing();
  } catch (error) {
    console.error('Error checking tracking status:', error);
    return false;
  }
};

export default {
  trackEvent,
  identifyUser,
  resetIdentity,
  trackPageView,
  setUserProperties,
  setCompanyProperties,
  optOut,
  optIn,
  isTrackingEnabled,
}; 